import {
  catchError,
  combineLatestWith,
  map,
  Observable,
  of,
  pipe,
  switchMap,
  tap,
} from 'rxjs';
import { StringLiteralCheck } from '../../util/string-literal-check';
import {
  createActionGroup,
  createFeature,
  createFeatureSelector,
  createReducer,
  createSelector,
  emptyProps,
  on,
  props,
  select,
  Store,
} from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { RemoteParamsMode, RemoteParamsState } from './remote-params.types';

// Note: if needed, cache expiration can easily be implemented by storing the
// last timestamp in the state

export interface RemoteParamsEffectConfig<T> {
  fetchParams: () => Observable<T>;
  notifyError?: () => void;
}

/**
 * @param featureName Literal string to be used as the feature name and actions source
 * @returns NGRX feature, actions, pipes, and effect builder.
 */
export function createRemoteParamsFeature<
  TActionSource extends string & StringLiteralCheck<TActionSource, 'source'>,
  T,
>(featureName: TActionSource) {
  const actions = createActionGroup({
    source: featureName,
    events: {
      ensure: props<{ suppressErrorNotification?: boolean }>(),
      clear: emptyProps(),
      fetch: props<{ suppressErrorNotification?: boolean }>(),
      success: props<{ data: T }>(),
      fail: props<{ suppressErrorNotification?: boolean }>(),
    },
  });
  const feature = createFeature({
    name: featureName,
    reducer: createReducer<RemoteParamsState<T>>(
      { mode: RemoteParamsMode.standBy },
      on(actions.fetch, () => ({
        mode: RemoteParamsMode.loading,
      })),
      on(actions.success, (state, action) => ({
        mode: RemoteParamsMode.loaded,
        data: action.data,
      })),
      on(actions.fail, () => ({
        mode: RemoteParamsMode.standBy,
      })),
      on(actions.clear, () => ({
        mode: RemoteParamsMode.standBy,
      })),
    ),
    extraSelectors: baseSelectors => ({
      selectIsLoading: createSelector(
        baseSelectors.selectMode,
        mode => mode === RemoteParamsMode.loading,
      ),
    }),
  });
  const selectFeature =
    createFeatureSelector<RemoteParamsState<T>>(featureName);
  const pipes = {
    selectData: pipe(
      select(selectFeature),
      switchMap(state =>
        state.mode === RemoteParamsMode.loaded ? of(state.data) : of(),
      ),
    ),
  };
  const createEffects = (
    action$: Actions,
    store: Store,
    config: RemoteParamsEffectConfig<T>,
  ) => {
    const { fetchParams, notifyError } = config;
    return {
      ensure$: createEffect(() =>
        action$.pipe(
          ofType(actions.ensure),
          combineLatestWith(store.select(feature.selectMode)),
          // Dispatch `fetch` if in the standBy mode, i. e. if data is not available and no fetching operation is in progress.
          switchMap(([action, mode]) => {
            const standingBy = mode === RemoteParamsMode.standBy;
            const { suppressErrorNotification } = action;
            return standingBy
              ? of(actions.fetch({ suppressErrorNotification }))
              : of();
          }),
        ),
      ),
      fetch$: createEffect(() =>
        action$.pipe(
          ofType(actions.fetch),
          switchMap(action =>
            fetchParams().pipe(
              map(data => actions.success({ data })),
              catchError(() =>
                of(
                  actions.fail({
                    suppressErrorNotification: action.suppressErrorNotification,
                  }),
                ),
              ),
            ),
          ),
        ),
      ),
      fail$: createEffect(
        () =>
          action$.pipe(
            ofType(actions.fail),
            tap(action => {
              if (!action.suppressErrorNotification) {
                notifyError?.();
              }
            }),
          ),
        { dispatch: false },
      ),
    };
  };
  return { feature, actions, createEffects, pipes };
}
